// src/components/Lightbox.js
// Full-screen photo viewer for the Full Gallery. Opens over a dark backdrop with
// prev/next controls, keyboard nav (Esc / arrows), and the photo caption.
// Rendered through a portal to <body> so it escapes the gallery's stacking and
// overflow context and sits above the fixed navbar.
import React, { useCallback, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import "./Lightbox.css";

const Lightbox = ({ images, index, onClose, onChange }) => {
  const count = images.length;
  const closeRef = useRef(null);

  const goPrev = useCallback(
    () => onChange((index - 1 + count) % count),
    [index, count, onChange]
  );
  const goNext = useCallback(
    () => onChange((index + 1) % count),
    [index, count, onChange]
  );

  // Keyboard: Esc closes, arrows step through the set.
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      else if (e.key === "ArrowLeft") goPrev();
      else if (e.key === "ArrowRight") goNext();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose, goPrev, goNext]);

  // Freeze the background while the viewer is open so the page doesn't scroll
  // behind it; restore whatever overflow was set before.
  useEffect(() => {
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previous;
    };
  }, []);

  // Pull focus into the dialog so keyboard users land on the controls.
  useEffect(() => {
    closeRef.current?.focus();
  }, []);

  const current = images[index];
  if (!current) return null;

  return createPortal(
    <div
      className="lightbox"
      role="dialog"
      aria-modal="true"
      aria-label="Photo viewer"
      onClick={onClose}
    >
      <button
        type="button"
        className="lightbox__close"
        aria-label="Close photo viewer"
        ref={closeRef}
        onClick={onClose}
      >
        &times;
      </button>

      {count > 1 && (
        <button
          type="button"
          className="lightbox__nav lightbox__nav--prev"
          aria-label="Previous photo"
          onClick={(e) => {
            e.stopPropagation();
            goPrev();
          }}
        >
          &lsaquo;
        </button>
      )}

      <figure
        className="lightbox__figure"
        onClick={(e) => e.stopPropagation()}
      >
        <img className="lightbox__image" src={current.src} alt={current.alt} />
        {current.caption && (
          <figcaption className="lightbox__caption">
            {current.caption}
          </figcaption>
        )}
      </figure>

      {count > 1 && (
        <button
          type="button"
          className="lightbox__nav lightbox__nav--next"
          aria-label="Next photo"
          onClick={(e) => {
            e.stopPropagation();
            goNext();
          }}
        >
          &rsaquo;
        </button>
      )}
    </div>,
    document.body
  );
};

export default Lightbox;
